import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import ConsentimientoDatos from '../components/clinica/ConsentimientoDatos';
import { CONSENTIMIENTO_OBLIGATORIO, CONSENTIMIENTO_OPCIONAL, CONSENTIMIENTO_VERSION } from '../config/consentimientoCuestionarios';
import {
  Stethoscope, CheckCircle, AlertCircle, ChevronRight,
  ChevronLeft, User, FileText, Edit3, Loader2, XCircle
} from 'lucide-react';

// Import questionnaire schemas
import {
  SF12_SCHEMA, DOLOR_MEFIR_SCHEMA, VAS_OFICIAL_SCHEMA,
  calculateSF12Score, calculateDolorScore, calculateVASScore
} from '../components/admin/clinica/questionnaireSchemas';
import {
  ODI_SCHEMA, EFP0_SCHEMA,
  calculateODIScore, calculateEFP0Score
} from '../components/admin/clinica/additionalQuestionnaires';
import {
  isPatientQuestionnaireSupported,
} from '../components/admin/clinica/supportedQuestionnaires';

// Build questionnaire schemas map
const questionnaireSchemas = {
  'vas_oficial': VAS_OFICIAL_SCHEMA,
  'sf12': SF12_SCHEMA,
  'dolor_mefir': DOLOR_MEFIR_SCHEMA,
  'odi': ODI_SCHEMA,
  'efp0': EFP0_SCHEMA,
};

// Build scoring functions map
const scoringFunctions = {
  'vas_oficial': calculateVASScore,
  'sf12': calculateSF12Score,
  'dolor_mefir': calculateDolorScore,
  'odi': calculateODIScore,
  'efp0': calculateEFP0Score,
};

const TabletCuestionario = () => {
  // Esta página sirve a DOS rutas con un único componente (no duplicar UI):
  //   - /tablet/cuestionario/:codigo  → flujo tablet (sesión 30 min)
  //   - /paciente/cuestionario/:token → flujo email (token 7 días, one-shot)
  // El parámetro presente decide qué endpoints backend consumir.
  const params = useParams();
  const isEmailMode = !!params.token;
  const sessionId = params.token || params.codigo;
  const sessionBaseUrl = isEmailMode
    ? `${API}/clinica/cuestionario/email-session/${sessionId}`
    : `${API}/clinica/cuestionario/tablet-session/${sessionId}`;
  const completeUrl = `${sessionBaseUrl}/completar`;

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  // Wizard state
  const [step, setStep] = useState('welcome'); // welcome, consent, signature, questions, success
  const [consentAccepted, setConsentAccepted] = useState(false);
  // Casilla OPCIONAL (uso anonimizado): no bloquea, pero se guarda siempre.
  const [consentInvestigacion, setConsentInvestigacion] = useState(false);
  const [signature, setSignature] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const sigCanvas = useRef(null);

  // Load session data
  useEffect(() => {
    loadSession();
  }, [sessionId]);

  const loadSession = async () => {
    try {
      const res = await axios.get(sessionBaseUrl);
      const data = res.data;
      // Defensa: si el cuestionario_tipo NO está soportado por este wizard,
      // mostramos error legible en vez de seguir hasta el step "questions"
      // y dejar pantalla vacía. Caso testigo 2026-05-20: admin lanzó
      // vas_oficial/eq5d5l por email; wizard no tenía schema → pantalla verde.
      const tipo = data?.cuestionario_tipo;
      if (!isPatientQuestionnaireSupported(tipo)) {
        console.error(
          `[TabletCuestionario] cuestionario_tipo no soportado por este wizard: "${tipo}". ` +
          `Soportados: vas_oficial, sf12, dolor_mefir, odi, efp0.`
        );
        setError('Cuestionario no disponible. Contacta con el centro.');
        setLoading(false);
        return;
      }
      setSessionData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading session:', err);
      // En modo email, 410 GONE = enlace no válido/caducado/completado (mensaje genérico timing-safe).
      const detail = err.response?.data?.detail;
      const fallback = isEmailMode
        ? 'Enlace no válido o caducado'
        : 'Error al cargar la sesión';
      setError(detail || fallback);
      setLoading(false);
    }
  };

  // Get questionnaire schema
  const getQuestionnaireSchema = () => {
    if (!sessionData) return null;
    const tipo = sessionData.cuestionario_tipo;
    return questionnaireSchemas[tipo];
  };

  const schema = getQuestionnaireSchema();
  // Flatten sections → questions (SF-12, ODI, etc. usan schema.sections[].questions[])
  const questions = React.useMemo(() => {
    if (!schema) return [];
    if (schema.questions) return schema.questions;
    const flat = [];
    (schema.sections || []).forEach(section => {
      (section.questions || []).forEach(q => {
        flat.push({ ...q, sectionTitle: section.title });
      });
    });
    return flat;
  }, [schema]);

  // Calculate progress
  const totalSteps = 4; // welcome, consent, signature, questions
  const stepProgress = {
    'welcome': 0,
    'consent': 25,
    'signature': 50,
    'questions': 75 + (questions.length > 0 ? (currentQuestionIndex / questions.length) * 25 : 0),
    'success': 100
  };

  // Handle signature
  const handleClearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    setSignature(null);
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataUrl = sigCanvas.current.toDataURL('image/png');
      setSignature(dataUrl);
      setStep('questions');
    } else {
      toast.error('Por favor, firme antes de continuar');
    }
  };

  // Handle question answers
  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const currentQuestion = questions[currentQuestionIndex];
  const isCurrentQuestionAnswered = currentQuestion && answers[currentQuestion.id] !== undefined;

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  // Calculate results and submit
  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Calculate scores using scoring functions
      const tipo = sessionData.cuestionario_tipo;
      let resultados = {};

      const scoreFn = scoringFunctions[tipo];
      if (scoreFn) {
        resultados = scoreFn(answers);
      }

      // Submit to backend
      await axios.post(completeUrl, {
        respuestas: answers,
        resultados: resultados,
        firma: signature,
        consentimiento_aceptado: consentAccepted,
        consentimiento: {
          aceptado: consentAccepted,
          texto: CONSENTIMIENTO_OBLIGATORIO,
          version: CONSENTIMIENTO_VERSION,
          uso_investigacion_anonimizado: consentInvestigacion,
          texto_investigacion: CONSENTIMIENTO_OPCIONAL
        }
      });

      setStep('success');
    } catch (err) {
      console.error('Error submitting:', err);
      // 410 GONE en modo email = link ya usado / caducado durante la sesión.
      if (isEmailMode && err.response?.status === 410) {
        setError(err.response?.data?.detail || 'Enlace no válido o caducado');
      } else {
        toast.error('Error al guardar las respuestas');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Render question based on type
  const renderQuestion = (question) => {
    const value = answers[question.id];

    switch (question.type) {
      case 'radio':
        return (
          <RadioGroup
            value={value?.toString()}
            onValueChange={(val) => handleAnswer(question.id, parseInt(val))}
            className="space-y-3"
          >
            {question.options.map((option, idx) => (
              <div
                key={idx}
                className={`flex items-center p-4 rounded-xl border-2 transition-all cursor-pointer ${value === option.value
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-200 hover:border-gray-300'
                  }`}
                onClick={() => handleAnswer(question.id, option.value)}
              >
                <RadioGroupItem value={option.value.toString()} id={`q-${question.id}-${idx}`} />
                <Label
                  htmlFor={`q-${question.id}-${idx}`}
                  className="ml-3 text-lg cursor-pointer flex-1"
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'scale':
        return (
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-gray-500 px-2">
              <span>{question.minLabel || 'Sin dolor'}</span>
              <span>{question.maxLabel || 'Dolor máximo'}</span>
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              {Array.from({ length: (question.max || 10) + 1 }, (_, i) => i).map((num) => (
                <button
                  key={num}
                  onClick={() => handleAnswer(question.id, num)}
                  className={`w-14 h-14 rounded-xl text-xl font-bold transition-all ${value === num
                      ? 'bg-teal-600 text-white shadow-lg scale-110'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        );

      case 'faces':
        const faces = ['😊', '🙂', '😐', '🙁', '😢'];
        return (
          <div className="flex gap-4 justify-center flex-wrap">
            {faces.map((face, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(question.id, idx)}
                className={`w-20 h-20 rounded-2xl text-4xl transition-all ${value === idx
                    ? 'bg-teal-100 ring-4 ring-teal-500 scale-110'
                    : 'bg-gray-100 hover:bg-gray-200'
                  }`}
              >
                {face}
              </button>
            ))}
          </div>
        );

      case 'text':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="Escriba su respuesta..."
            className="min-h-32 text-lg"
          />
        );

      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 text-teal-600 animate-spin" />
            <p className="text-gray-600">Cargando cuestionario...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {isEmailMode ? 'Enlace no válido' : 'Sesión no válida'}
            </h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">
              {isEmailMode
                ? 'Por favor, contacta con tu clínica para que te envíen un nuevo enlace.'
                : 'Por favor, solicita un nuevo código QR a tu terapeuta.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 to-emerald-600 p-4 md:p-8">
      {/* Progress bar */}
      <div className="max-w-3xl mx-auto mb-6">
        <Progress value={stepProgress[step]} className="h-2 bg-white/30" />
      </div>

      {/* Welcome Step */}
      {step === 'welcome' && (
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Stethoscope className="w-10 h-10 text-white" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Bienvenido/a
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              {sessionData?.paciente_nombre}
            </p>

            <div className="p-4 bg-teal-50 rounded-xl mb-8">
              <p className="text-lg text-teal-800">
                Tu terapeuta te ha asignado el cuestionario:
              </p>
              <p className="text-xl font-semibold text-teal-700 mt-1">
                {schema?.title || sessionData?.cuestionario_tipo}
              </p>
            </div>

            <p className="text-gray-500 mb-8">
              Este cuestionario nos ayudará a entender mejor tu situación y personalizar tu tratamiento.
            </p>

            <Button
              onClick={() => setStep('consent')}
              size="lg"
              className="h-14 px-8 text-lg bg-teal-600 hover:bg-teal-700"
            >
              Comenzar
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Consent Step */}
      {step === 'consent' && (
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="border-b bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-teal-600" />
              Consentimiento Informado
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Texto legal ÚNICO (config/consentimientoCuestionarios.js) */}
            <ConsentimientoDatos
              aceptado={consentAccepted}
              onAceptar={setConsentAccepted}
              investigacion={consentInvestigacion}
              onInvestigacion={setConsentInvestigacion}
              grande
            />

            <div className="flex gap-3 mt-8">
              <Button
                variant="outline"
                onClick={() => setStep('welcome')}
                className="h-12"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Volver
              </Button>
              <Button
                onClick={() => setStep('signature')}
                disabled={!consentAccepted}
                className="flex-1 h-12 text-lg bg-teal-600 hover:bg-teal-700"
              >
                Continuar
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature Step */}
      {step === 'signature' && (
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="border-b bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Edit3 className="w-6 h-6 text-teal-600" />
              Firma Digital
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-600 mb-4 text-center">
              Por favor, firme en el recuadro inferior
            </p>

            <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white mb-4">
              <SignatureCanvas
                ref={sigCanvas}
                canvasProps={{
                  className: 'w-full h-48',
                  style: { width: '100%', height: '200px' }
                }}
                backgroundColor="white"
              />
            </div>

            <div className="flex gap-3 mb-6">
              <Button
                variant="outline"
                onClick={handleClearSignature}
                className="flex-1"
              >
                Borrar firma
              </Button>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep('consent')}
                className="h-12"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Volver
              </Button>
              <Button
                onClick={handleSaveSignature}
                className="flex-1 h-12 text-lg bg-teal-600 hover:bg-teal-700"
              >
                Guardar y Continuar
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Questions Step */}
      {step === 'questions' && currentQuestion && (
        <Card className="max-w-2xl mx-auto">
          <CardHeader className="border-b bg-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {schema?.title || 'Cuestionario'}
              </CardTitle>
              <span className="text-sm text-gray-500">
                Pregunta {currentQuestionIndex + 1} de {questions.length}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {currentQuestion.text}
              </h2>

              {renderQuestion(currentQuestion)}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handlePrevQuestion}
                disabled={currentQuestionIndex === 0}
                className="h-12"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Anterior
              </Button>

              {currentQuestionIndex < questions.length - 1 ? (
                <Button
                  onClick={handleNextQuestion}
                  disabled={!isCurrentQuestionAnswered}
                  className="flex-1 h-12 text-lg bg-teal-600 hover:bg-teal-700"
                >
                  Siguiente
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!isCurrentQuestionAnswered || submitting}
                  className="flex-1 h-12 text-lg bg-emerald-600 hover:bg-emerald-700"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Finalizar
                      <CheckCircle className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Step */}
      {step === 'success' && (
        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              ¡Completado!
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              Gracias, {sessionData?.paciente_nombre}
            </p>

            <div className="p-4 bg-green-50 rounded-xl mb-8">
              <p className="text-lg text-green-800">
                Tus respuestas han sido guardadas correctamente.
              </p>
              <p className="text-green-600 mt-2">
                Tu terapeuta revisará los resultados contigo.
              </p>
            </div>

            <p className="text-gray-500">
              Puedes cerrar esta ventana o devolver la tablet a tu terapeuta.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TabletCuestionario;
